import { useCallback } from 'react'
import { ListBox, ListBoxItem } from 'react-aria-components'
import { resolvePath, useLocation, useNavigate } from 'react-router-dom'
import Panel, { PanelSection } from '~/components/UIKit/Panel'
import { getSnapshotUrl } from '~/lib/utils'
import { SNAPSHOTS_PANEL } from '~/store/panel/types'
import { Icon } from '~/ui/Icon'
import { Spinner } from '~/ui/Spinner'

type Snapshot = {
  id: string
  cloudinaryId: string
  cropData: any
  data: any
  title: string
}

export default function SnapshotsPanel({
  onSnapshot,
  snapshots,
  uploadingSnapshot,
}: {
  onSnapshot(data: string): void
  snapshots: Snapshot[]
  uploadingSnapshot: boolean
}) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const takeSnapshot = useCallback(() => {
    const canvas = document.querySelector('#drawing-canvas canvas')
    if (!(canvas instanceof HTMLCanvasElement)) {
      throw new Error('drawing canvas is missing')
    }
    canvas.animate(
      {
        filter: ['brightness(4.0)', 'brightness(1.0)'],
        easing: ['ease-out', 'ease-in'],
      },
      200
    )
    const shot = canvas.toDataURL()
    onSnapshot(shot)
  }, [onSnapshot])

  return (
    <Panel title="Snapshots" landscape panelKey={SNAPSHOTS_PANEL}>
      <PanelSection>
        <ListBox
          orientation="horizontal"
          className="flex flex-row gap-2 overflow-x-auto overflow-y-none"
          aria-label="Snapshots"
        >
          <ListBoxItem
            onAction={takeSnapshot}
            isDisabled={uploadingSnapshot}
            textValue="Add New"
            className="cursor-pointer disabled:cursor-progress disabled:opacity-50"
          >
            <div className="h-full min-h-[80px] w-[140px] grid place-content-center text-center place-items-center">
              {uploadingSnapshot ? <Spinner size={28} /> : <Icon name="plus" size={28} />}
              <div>Add New</div>
            </div>
          </ListBoxItem>
          {snapshots.map(snapshot => {
            return (
              <ListBoxItem
                key={snapshot.id}
                textValue={snapshot.title}
                onAction={() => navigate(resolvePath(`snapshot/${snapshot.id}`, pathname))}
                className="cursor-pointer"
              >
                <img className="h-[80px] rounded border border-zinc-500 shadow-md" src={getSnapshotUrl(snapshot)} />
                <div className="w-[140px] text-center">{snapshot.title}</div>
              </ListBoxItem>
            )
          })}
        </ListBox>
      </PanelSection>
    </Panel>
  )
}
